<template>
  <v-lazy
    :min-height="isOnMap ? 100 : 200"
    :options="{ threshold: 0.5 }"
    transition="fade-transition"
    class="rentpress-property-card"
  >
    <a
      :href="property.property_post_link"
      @click="
        gtag('event', 'viewproperty_click_rentpress', {
          property_name: property.property_name,
          city: property.property_city
        })
      "
    >
      <v-card
        :width="isOnMap ? '250' : '410'"
        hover
        :tile="hideImage || isOnMap"
        :class="[
          'rentpress-inherited-font-family',
          !isOnMap
            ? 'rentpress-property-card-style'
            : 'rentpress-property-card-style-map'
        ]"
      >
        <v-img
          v-if="!hideImage"
          :src="featuredImage"
          :srcset="featuredImageSrcset"
          :height="isOnMap ? '120px' : '245px'"
          max-width="100%"
          :sizes="isOnMap ? '250px' : '(max-width: 500px) 500px, 410px'"
          :aspect-ratio="isOnMap ? 1.66 : 1.67"
        >
          <template v-slot:placeholder>
            <div class="d-flex align-center justify-center fill-height">
              <v-progress-circular
                color="primary"
                indeterminate
              ></v-progress-circular>
            </div>
          </template>
          <v-banner
            v-if="
              property.property_specials_message &&
                property.property_specials_expiration >= todaysDate
            "
            elevation="8"
            class="rentpress-property-card-special-banner"
            :style="{
              color: getContrastingTextColor(propertyAccentColor(property)),
              backgroundColor: propertyAccentColor(property) + 'BF',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }"
          >
            <div class="special-banner-content">
              <v-icon
                class="property-card-special-icon"
                :style="{
                  color: getContrastingTextColor(propertyAccentColor(property))
                }"
              >
                {{ $vuetify.icons.values.star }}
              </v-icon>
              <span class="special-text-wrapper">
                <strong>SPECIAL:</strong>
                {{ property.property_specials_message }}
              </span>
            </div>
          </v-banner>
        </v-img>
        <div class="rentpress-property-card-pet-icons-container">
          <v-sheet
            v-if="allowsCats"
            class="rounded-circle"
            elevation="4"
            :height="32"
            :width="32"
            top
            right
          >
            <v-icon class="px-1 py-1" color="black">{{
              $vuetify.icons.values.cat
            }}</v-icon>
          </v-sheet>
          <v-sheet
            v-if="allowsDogs"
            class="rounded-circle"
            elevation="4"
            :height="32"
            :width="32"
            top
            right
          >
            <v-icon class="px-1 py-1" color="black">{{
              $vuetify.icons.values.dog
            }}</v-icon>
          </v-sheet>
        </div>
        <v-card-title
          primary-title
          class="
          rentpress-inherited-font-family
          text-h6
          text-left
          font-weight-bold
          rentpress-no-wrap-text
          pb-0
          rentpress-property-card-title
        "
          v-html="property.property_name"
        >
        </v-card-title>
        <v-card-subtitle
          align="left"
          class="rentpress-inherited-font-family text-body-1 mt-n2 rentpress-property-card-subtitle"
          v-html="subtitleText"
        />
        <v-card-text
          v-if="!disablePrice && showPriceMatrix && differentPrices"
          class="rentpress-infocard-matrix-grid-container rentpress-property-card-bed-price"
        >
          <div class="rentpress-property-card-matrix-grid">
            <div
              v-for="(availablePrice, bedIndex) in priceMatrix"
              :key="bedIndex"
            >
              <div
                v-html="availablePrice.name"
                class="font-weight-black rentpress-property-card-matrix-beds"
              />
              <div
                v-html="
                  availablePrice.price
                    ? formatter.format(availablePrice.price) + '+'
                    : ''
                "
                class="rentpress-property-card-matrix-price"
              />
            </div>
          </div>
          <div
            v-if="badPrice"
            v-html="
              options.rentpress_disable_pricing_section_disable_pricing_message
            "
            class="rentpress-property-card-price-message"
          />
        </v-card-text>
        <v-card-text
          v-else
          class="rentpress-inherited-font-family text-body-1 rentpress-property-card-bed-price"
        >
          <div v-if="displayBedTypes" class="rentpress-property-card-bedrooms">
            <span class="font-weight-black">{{ displayBedTypes }}</span>
            <span v-if="displayBedTypes != 'Studio'"> Bedroom</span>
          </div>
          <div
            v-if="!disablePrice && displayPrice"
            class="rentpress-property-card-price"
          >
            <span v-html="displayPrice"></span>
          </div>
          <div v-else class="rentpress-property-card-price-message">
            <span class="font-italic">
              {{
                options.rentpress_disable_pricing_section_disable_pricing_message
              }}
            </span>
          </div>
        </v-card-text>
        <script v-html="jsonld" type="application/ld+json"></script>
      </v-card>
    </a>
  </v-lazy>
</template>
<script>
export default {
  name: "property-card",
  data: function() {
    return {
      bed_types: [],
      priceMatrix: [],
      termsList: [],
      badPrice: true,
      displayBedTypes: "",
      disablePrice: this.options
        .rentpress_disable_pricing_section_disable_pricing,
      displayPrice: "",
      showPriceMatrix: this.options.show_matrix,
      featuredImage: this.setFeaturedImage(),
      featuredImageSrcset: this.setFeaturedImageSrcset(),
      subtitleText: "",
      allowsDogs: this.checkForValueInTerms("Dog"),
      allowsCats: this.checkForValueInTerms("Cat"),
      differentPrices: false,
      currentPrice: null,
      todaysDate: "",
      jsonld: {
        "@context": "https://schema.org/",
        "@type": "ApartmentComplex",
        address: {
          "@type": "PostalAddress",
          streetAddress: this.property.property_address,
          addressLocality: this.property.property_city,
          addressRegion: this.property.property_state,
          postalCode: this.property.property_zip
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: this.property.property_latitude,
          longitude: this.property.property_longitude
        },
        telephone: this.property.property_phone_number,
        image: this.property.property_featured_image_src,
        name: this.property.property_name,
        numberOfAvailableAccommodationUnits: this.property
          .property_available_floorplans,
        tourBookingPage: this.property.property_tour_url,
        amenityFeature: this.property.property_terms,
        petsAllowed: this.displayPetText,
        url: this.property.property_post_link,
        sameAs: [
          this.property.property_website,
          this.property.property_facebook_link,
          this.property.property_instagram_link
        ]
      }
    };
  },
  props: {
    property: Object,
    options: Object,
    hideImage: {
      type: Boolean,
      default: false
    },
    isOnMap: {
      type: Boolean,
      default: false
    },
    formatter: Intl.NumberFormat
  },
  methods: {
    propertyAccentColor(property) {
      return Number(property.property_use_accent_color) === 1
        ? property.property_accent_color
        : this.options.rentpress_accent_color_section_input;
    },
    getContrastingTextColor(color) {
      if (!color) return "#202124";
      const c = color.replace("#", "");
      const rgb = parseInt(c, 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? "#202124" : "#FDFDFD";
    },
    // Determines if a color is dark enough to contrast on white, courtesy WCAG
    isColorDarkEnough(color) {
      if (!color) return false;
      const c = color.replace("#", "");
      const rgb = parseInt(c, 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      // If brightness is less than or equal to 128, it's dark enough
      return brightness <= 128;
    },
    navigateToPost: function(post_id) {
      window.location = "/?page_id=" + post_id;
    },
    setFeaturedImage() {
      return this.property.property_featured_image_src &&
        this.property.property_featured_image_src !== ""
        ? this.property.property_featured_image_src
        : this.options.rentpress_default_property_image_section;
    },
    setFeaturedImageSrcset() {
      return this.property.property_featured_image_srcset &&
        this.property.property_featured_image_srcset !== ""
        ? this.property.property_featured_image_srcset
        : this.options.rentpress_default_property_image_section;
    },
    setSubtitle() {
      const neighborhood = this.property.property_neighborhood_post_name || "";
      const city = this.property.property_city || "";
      const state = this.property.property_state || "";
      const cityState =
        city && state ? `${city}, ${state}` : city || state || "";
      this.subtitleText = `
      <span class="rentpress-property-card-neighborhood">
        ${neighborhood ? neighborhood + ", " : ""}
      </span>
      <span class="rentpress-property-card-city-state">
        ${cityState}
      </span>
      `.trim();
    },
    checkForValueInTerms(value) {
      let termStr = this.property.property_terms;
      const catRE = /\b[cC]+[aA]+[tT]+[sS]?\b/;
      const dogRE = /\b[dD]+[oO]+[gG]+[sS]?\b/;

      if (termStr) {
        if (value === "Cat") {
          return catRE.exec(termStr) !== null;
        }
        if (value === "Dog") {
          return dogRE.exec(termStr) !== null;
        }
      }
      return false;
    },
    setTodaysDate() {
      const today = new Date();
      this.todaysDate =
        today.getFullYear() +
        "-" +
        (today.getMonth() + 1) +
        "-" +
        today.getDate();
    },
    setDefaultvalues() {
      if (this.property.property_bed_types) {
        this.bed_types = JSON.parse(this.property.property_bed_types).sort();
      }

      if (this.bed_types.length && !this.bed_types.includes("a:0:{}")) {
        if (this.bed_types[0] === 0) {
          this.displayBedTypes = "Studio";
        } else {
          this.displayBedTypes = this.bed_types[0];
        }

        if (this.bed_types.length > 1) {
          this.displayBedTypes +=
            " - " + this.bed_types[this.bed_types.length - 1];
        }
      }

      if (this.property.property_availability_matrix) {
        this.priceMatrix = JSON.parse(
          this.property.property_availability_matrix
        );
        Object.values(this.priceMatrix).forEach(bedType => {
          if (bedType.price && bedType.price > 100) {
            this.badPrice = false;
          }
          if (this.currentPrice && this.currentPrice !== bedType.price) {
            this.differentPrices = true;
          } else if (!this.currentPrice) {
            this.currentPrice = bedType.price;
          }
        });
      }

      if (
        this.property.property_rent_type_selection === "Minimum - Maximum" &&
        this.property.property_rent_min &&
        this.property.property_rent_max &&
        this.property.property_rent_min !== this.property.property_rent_max
      ) {
        this.displayPrice =
          "<b>" +
          this.formatter.format(this.property.property_rent_min) +
          " - " +
          this.formatter.format(this.property.property_rent_max) +
          "</b>";
      } else if (
        this.property.property_rent_type_selection_cost &&
        this.property.property_rent_type_selection_cost > 100
      ) {
        this.displayPrice =
          "<b>" +
          this.formatter.format(
            this.property.property_rent_type_selection_cost
          ) +
          "</b> Starting Price";
      }
    }
  },
  watch: {
    property() {
      this.currentPrice = null;
      this.differentPrices = false;
      this.displayPrice = "";
      this.allowsDogs = this.checkForValueInTerms("Dog");
      this.allowsCats = this.checkForValueInTerms("Cat");
      this.featuredImage = this.setFeaturedImage();
      this.featuredImageSrcset = this.setFeaturedImageSrcset();
      this.setDefaultvalues();
      this.setSubtitle();
    }
  },
  mounted: function() {
    this.setDefaultvalues();
    this.setSubtitle();
  }
};
</script>

<style lang="css">
.rentpress-property-card-style {
  outline: 2px solid #e6e6e64d;
  border-radius: 5px;
  height: 400px;
}

.v-banner__wrapper {
  border: none !important;
}

.rentpress-property-card-special-banner .v-banner__wrapper {
  padding: 0 !important;
}

.rentpress-property-card-special-banner {
  display: flex;
  align-items: flex-start;
  padding: 0.7em;
  font-weight: 500;
  word-break: break-word;
  color: inherit;
}

.rentpress-property-card .special-banner-content {
  display: flex;
  align-items: flex-start;
  gap: 0; /* remove gap between icon and text */
}

.property-card-special-icon {
  flex-shrink: 0;
  font-size: 1.2em;
  line-height: 1;
  margin: -0.15em 0 0 0; /* nudge icon slightly upwards */
}

.rentpress-property-card {
  max-width: 410px;
  margin: auto;
}

.rentpress-property-card .special-text-wrapper {
  display: inline;
  margin: 0;
}

.rentpress-property-card .special-text-wrapper strong {
  margin: 0;
}

.rentpress-property-card-pet-icons-container {
  position: absolute;
  margin-top: -16px;
  right: 0.5em;
  display: flex;
  gap: 0.5em;
}
.rentpress-property-card-pet-icons-container .v-icon.v-icon {
  display: inherit;
}
.rentpress-property-card-matrix-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, 4rem);
  justify-items: start;
}
.gm-style-iw.gm-style-iw-c {
  padding: 0 !important;
}

.gm-style-iw-d,
.gm-style-iw-ch {
  overflow: hidden !important;
  padding: 0 !important;
}

.gm-style-iw-c .gm-style-iw-tc {
  display: none;
}

button.gm-ui-hover-effect {
  position: absolute !important;
  top: 0px !important;
  right: 0px !important;
  background: #ffffffb5 !important;
  border-radius: 50% !important;
  margin: 5px !important;
  z-index: 1000 !important;
  width: 30px !important;
  height: 30px !important;
  opacity: 0.9;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

button.gm-ui-hover-effect:hover {
  background: #fff !important;
}

button.gm-ui-hover-effect span {
  margin: 3px !important;
}

@media only screen and (max-width: 400px) {
  .rentpress-map-infocard .rentpress-property-card-matrix-grid {
    /* display: grid; */
  }
}
</style>
